import React from 'react';
import styled from 'styled-components';

interface FilterChipProps {
  label?: string;
  value: string;
  active?: boolean;
  variant?: 'default' | 'terracotta';
  /** Optional color swatch dot shown before the value */
  swatchColor?: string;
  /** Show a remove (×) button */
  onRemove?: () => void;
  onClick?: () => void;
}

export const FilterChip: React.FC<FilterChipProps> = ({
  label,
  value,
  active = false,
  variant = 'default',
  swatchColor,
  onRemove,
  onClick
}) => {
  const isTerra = variant === 'terracotta' || active;
  return (
    <Chip $terra={isTerra} onClick={onClick}>
      {label && <ChipLabel $terra={isTerra}>{label}</ChipLabel>}
      {swatchColor && <Swatch $color={swatchColor} />}
      {value}
      {onRemove ? (
        <RemoveBtn
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          aria-label="Remove"
        >
          ×
        </RemoveBtn>
      ) : (
        <Chevron $terra={isTerra}>▾</Chevron>
      )}
    </Chip>
  );
};

export const FilterSeparator = styled.div`
  width: 1px;
  height: 18px;
  background: var(--line);
`;

const Chip = styled.button<{ $terra: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 13px;
  border-radius: var(--radius-pill);
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s;
  border: 1px solid ${({ $terra }) => ($terra ? 'transparent' : 'transparent')};
  background: ${({ $terra }) =>
    $terra ? 'var(--terracotta)' : 'var(--paper)'};
  color: ${({ $terra }) => ($terra ? 'var(--white)' : 'var(--ink)')};
  font-family: var(--font-sans);

  &:hover {
    background: ${({ $terra }) =>
      $terra ? 'var(--terracotta-dark)' : 'var(--paper-warm)'};
    border-color: ${({ $terra }) => ($terra ? 'transparent' : 'var(--line)')};
  }
`;

const ChipLabel = styled.span<{ $terra: boolean }>`
  font-size: 10.5px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: ${({ $terra }) =>
    $terra ? 'var(--terracotta-soft)' : 'var(--ink-faint)'};
  font-weight: 600;
  margin-right: 2px;
`;

const Chevron = styled.span<{ $terra: boolean }>`
  color: ${({ $terra }) =>
    $terra ? 'var(--terracotta-soft)' : 'var(--ink-faint)'};
  font-size: 9px;
`;

const Swatch = styled.span<{ $color: string }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${({ $color }) => $color};
  flex-shrink: 0;
`;

const RemoveBtn = styled.span`
  font-size: 14px;
  line-height: 1;
  color: var(--ink-faint);
  cursor: pointer;
  margin-left: 2px;

  &:hover {
    color: var(--ink);
  }
`;
